import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import "./../../../assets/css/general/HelpSupport/HelpSupport.css";

// ── FAQ Data ─────────────────────────────────────────────────────────────────
const FAQ_DATA = [
  {
    id: 1,
    question: "How do I choose a mentor for my course?",
    answer:
      "Go to My Mentors from your student dashboard. You can browse available mentors filtered by course, check their profile, availability, and bio — then send a mentorship request directly.",
  },
  {
    id: 2,
    question: "What is the AI Tutor and how does it work?",
    answer:
      "The AI Tutor is an intelligent assistant that answers your coding questions, explains concepts, gives hints for challenges, and provides instant feedback. Access it anytime from the Ask AI section on your dashboard.",
  },
  {
    id: 3,
    question: "How do I track my roadmap progress?",
    answer:
      "From your dashboard, navigate to Roadmap Progress, select your subject, and you'll see a visual breakdown of completed topics, current milestone, and what's coming next.",
  },
  {
    id: 4,
    question: "Can I report a mentor?",
    answer:
      "Yes. Go to My Mentors on your dashboard, find the mentor, and use the report button. Your report will be reviewed by the admin who can activate or deactivate users based on such reports.",
  },
  {
    id: 5,
    question: "How does mentor verification work?",
    answer:
      "When a mentor applies, their application goes through a review process managed by the admin. Status moves from pending → under review → approved. Only approved mentors appear to students.",
  },
];

// ── Quick Topic Cards Data ────────────────────────────────────────────────────
const TOPICS = [
  {
    id: 1,
    title: "Account & Access",
    desc: "Login issues, signup, password reset",
    colorClass: "qc-blue",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    id: 2,
    title: "Courses & Roadmaps",
    desc: "Enrollment, progress, course content",
    colorClass: "qc-teal",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    ),
  },
  {
    id: 3,
    title: "Mentor Sessions",
    desc: "Booking, scheduling, session issues",
    colorClass: "qc-purple",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
  },
  {
    id: 4,
    title: "AI Tutor Help",
    desc: "AI features, challenges, feedback",
    colorClass: "qc-amber",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4l3 3" />
      </svg>
    ),
  },
  {
    id: 5,
    title: "Assignments",
    desc: "Submission, deadlines, grades",
    colorClass: "qc-blue",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
      </svg>
    ),
  },
  {
    id: 6,
    title: "Technical Issues",
    desc: "Bugs, errors, platform problems",
    colorClass: "qc-teal",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
];

// ── FAQ Item Component ────────────────────────────────────────────────────────
const FaqItem = ({ item }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`faq-item ${open ? "open" : ""}`}>
      <button className="faq-q" onClick={() => setOpen((p) => !p)}>
        {item.question}
        <span className="faq-arrow">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      {open && <div className="faq-a">{item.answer}</div>}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const HelpSupport = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "Student",
    topic: "Account & Access",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };
const navigate = useNavigate();

  return (
    
    <div className="help-page">
      {/* ── HERO ── */}
      <div className="help-hero">
        <h1>How can we help you?</h1>
        <p>
          Find answers, browse FAQs, or reach out to our support team. We're
          here to help you succeed.
        </p>
        <div className="help-search-wrap">
          <input
            type="text"
            placeholder="Search for help topics, FAQs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="help-search-btn">Search</button>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <Container className="help-content" fluid="lg">
        {/* Quick Topics */}
        <p className="section-label">Browse by topic</p>
        <Row className="g-3 mb-5">
          {TOPICS.map((topic) => (
            <Col key={topic.id} xs={6} sm={4} md={4} lg={2}>
              <div className="quick-card">
                <div className={`qc-icon ${topic.colorClass}`}>
                  {topic.icon}
                </div>
                <h3>{topic.title}</h3>
                <p>{topic.desc}</p>
              </div>
            </Col>
          ))}
        </Row>

        {/* FAQ */}
        <p className="section-label">Frequently asked questions</p>
        <div className="faq-list mb-5">
          {FAQ_DATA.map((item) => (
            <FaqItem key={item.id} item={item} />
          ))}
        </div>

        {/* Contact */}
        <p className="section-label">Get in touch</p>
        <Row className="g-4 mb-5">
          {/* Form */}
          <Col md={7}>
            <div className="contact-card">
              <h3>Send us a message</h3>
              <p>
                Describe your issue and our support team will respond promptly.
              </p>

              {submitted && (
                <div className="success-alert">
                  Your request has been submitted! Admin will review and respond
                  soon.
                </div>
              )}

              <Form onSubmit={handleSubmit}>
                <Row className="g-3">
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Full name</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={handleFormChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Role</Form.Label>
                      <Form.Select
                        name="role"
                        value={formData.role}
                        onChange={handleFormChange}
                      >
                        <option>Student</option>
                        <option>Mentor</option>
                        <option>Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Topic</Form.Label>
                      <Form.Select
                        name="topic"
                        value={formData.topic}
                        onChange={handleFormChange}
                      >
                        <option>Account & Access</option>
                        <option>Courses & Roadmaps</option>
                        <option>Mentor Sessions</option>
                        <option>AI Tutor</option>
                        <option>Technical Issue</option>
                        <option>Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Message</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="message"
                        rows={4}
                        placeholder="Describe your issue in detail..."
                        value={formData.message}
                        onChange={handleFormChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <button type="submit" className="submit-btn">
                      Submit Request
                    </button>
                  </Col>
                </Row>
              </Form>
            </div>
          </Col>

          {/* Contact Info */}
          <Col md={5}>
            <div className="contact-card h-100">
              <h3>Other ways to reach us</h3>
              <p>Choose the channel that works best for you.</p>

              <div className="ci-row">
                <div className="ci-icon ci-b">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <div>
                  <strong>Live Chat</strong>
                  <span>Available 9am – 6pm PKT, Mon–Fri</span>
                </div>
              </div>

              <div className="ci-row">
                <div className="ci-icon ci-t">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div>
                  <strong>Email Support</strong>
                  <span>support@techmentorhub.com</span>
                </div>
              </div>

              <div className="ci-row">
                <div className="ci-icon ci-a">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line
                      x1="12"
                      y1="17"
                      x2="12.01"
                      y2="17"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <div>
                  <strong>Help Center</strong>
                  <span>Browse 100+ articles and guides</span>
                </div>
              </div>

              <div className="admin-note">
                <strong>Admin-managed support:</strong> All submitted tickets
                are reviewed and handled by the system admin. You'll receive a
                notification once your request is updated.
              </div>
              <button
                className="helpsupport-back-btn"
                onClick={() => navigate("/home")}
              >
                ← Back
              </button>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default HelpSupport;
